import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import { MdInventory2, MdTab } from 'react-icons/md';
import { AUDIENCE_OPTIONS, Field, FormHeader, GHOST_BTN, ProductPicker, STATUS_OPTIONS, Section, YELLOW_BTN } from './formShared';

const emptyTab = { name: '', status: 'active', position: '', showTo: 'all', product_id: [] };

const TabForm = ({ open, handleClose, handleSubmit, isEdit, editingTab }) => {
  const [formData, setFormData] = useState(emptyTab);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setErrors({});
    if (isEdit && editingTab) {
      setFormData({
        name: editingTab.name || '',
        status: editingTab.status?.toLowerCase() || 'active',
        position: editingTab.position?.toString() || '',
        showTo: editingTab.showTo?.toLowerCase() || 'all',
        product_id: Array.isArray(editingTab.product_ids)
          ? editingTab.product_ids
          : Array.isArray(editingTab.products)
            ? editingTab.products.map((p) => p._id || p.id || p)
            : [],
      });
    } else {
      setFormData(emptyTab);
    }
  }, [isEdit, editingTab, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!formData.name.trim()) next.name = 'Give this tab a name';
    if (formData.position === '' || Number(formData.position) < 1) next.position = 'Enter a position of 1 or more';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      // the page closes this form itself once the save succeeds
      await handleSubmit?.({ ...formData, name: formData.name.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <FormHeader
        title={isEdit ? 'Edit Tab' : 'Add Tab'}
        subtitle="A tab on the home screen that groups products for one audience."
        onBack={handleClose}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Section title="Tab details" hint="Name, order and who sees it" accent="#10b981" icon={MdTab}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Field label="Tab name" name="name" value={formData.name} onChange={handleChange} required error={!!errors.name} helperText={errors.name} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="Status" name="status" value={formData.status} onChange={handleChange} options={STATUS_OPTIONS} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="Position" name="position" type="number" inputProps={{ min: 1 }} value={formData.position} onChange={handleChange} required error={!!errors.position} helperText={errors.position || 'Lower numbers appear first'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="Show to" name="showTo" value={formData.showTo} onChange={handleChange} options={AUDIENCE_OPTIONS} />
            </Grid>
          </Grid>
        </Section>

        <Section title="Products" hint="Tick the products that belong in this tab" accent="#f59e0b" icon={MdInventory2}>
          <ProductPicker
            selectedIds={formData.product_id}
            onChange={(ids) => setFormData((prev) => ({ ...prev, product_id: ids }))}
            known={editingTab?.products || []}
          />
        </Section>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={handleClose} sx={GHOST_BTN}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={YELLOW_BTN}>
            {saving ? <><CircularProgress size={18} sx={{ color: 'black', mr: 1 }} />Saving…</> : isEdit ? 'Update tab' : 'Create tab'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TabForm;
